import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { Options } from 'flatpickr/dist/types/options';

enum DatePickerLocale {
    'de' = 1,
    'fr',
    'it',
    'en',
}

type DatePickerLocaleKey = keyof typeof DatePickerLocale;
type DatePickerLocaleValue = (typeof DatePickerLocale)[DatePickerLocaleKey];

export default class TKFormTimepicker extends TKCustomElementFactory {
    fieldElement?: HTMLInputElement;
    iconElement?: HTMLSpanElement;
    labelElement?: HTMLLabelElement;
    dateRangePicker?: Instance;
    minTime?: string;
    maxTime?: string;
    localeKey: DatePickerLocaleKey;
    locale: DatePickerLocaleValue;
    timeFormat: string;
    defaultHour: number;
    defaultMinute: number;
    allowMobile: boolean;

    constructor() {
        super();

        this.fieldElement = this.querySelector('input') || undefined;
        this.iconElement = this.querySelector('[data-tk-addon]') || undefined;
        this.labelElement = this.querySelector('label') || undefined;
        this.minTime = this.getAttribute('data-tk-min-time')!;
        this.maxTime = this.getAttribute('data-tk-max-time')!;
        this.localeKey = (this.getAttribute('data-tk-locale') || 1) as DatePickerLocaleKey;
        this.locale = DatePickerLocale[this.localeKey];
        this.timeFormat = this.getAttribute('data-tk-time-format') || 'H:i';
        this.defaultHour = Number(this.getAttribute('data-tk-default-hour')) || 12;
        this.defaultMinute = Number(this.getAttribute('data-tk-default-minute')) || 0;
        this.allowMobile = this.hasAttribute('data-tk-allow-mobile');
    }

    connectedCallback() {
        if (!this.fieldElement) return;

        this.dateRangePicker = flatpickr(this.fieldElement, {
            enableTime: true,
            noCalendar: true,
            time_24hr: true,
            dateFormat: this.timeFormat,
            minTime: this.minTime,
            maxTime: this.maxTime,
            locale: this.locale,
            defaultHour: this.defaultHour,
            defaultMinute: this.defaultMinute,
            disableMobile: !this.allowMobile,
            onReady(selectedDates: string[], dateStr: string, instance: Instance) {
                const calendarElement: HTMLElement = instance.calendarContainer;
                calendarElement.classList.add('closed');
            },
            onChange(selectedDates: string[], dateStr: string, instance: Instance) {
                const calendarElement: HTMLElement = instance.calendarContainer;

                // Required to prevent close and open at the same time
                const missingClassClosed = !calendarElement.classList.contains('open')
                && !calendarElement.classList.contains('closed');

                if (missingClassClosed) {
                    calendarElement.classList.add('closed');
                }
            },
        } as unknown as Options);

        this.registerClickListener();
    }

    registerClickListener() {
        if (!this.iconElement) throw new Error('Missing calendar icon');
        if (!this.fieldElement) throw new Error('Missing input element');
        if (!this.labelElement) throw new Error('Missing label element');

        const clickedHandler = this.onClicked.bind(this);
        const documentClickedHandler = this.onDocumentClick.bind(this);
        this.pushListener({ event: 'click', element: this.iconElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: this.fieldElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: this.labelElement, action: clickedHandler });
        this.pushListener({ event: 'click', element: document, action: documentClickedHandler });
    }

    onClicked(event: MouseEvent) {
        if (!this.dateRangePicker) return;

        // Necessary so that the default behavior is suppressed when clicking on the label
        if ((event.target instanceof HTMLLabelElement) && (event.target.closest('tk-form-timepicker'))) {
            event.preventDefault();
        }

        const { calendarContainer } = this.dateRangePicker;

        const isOpen = !calendarContainer.classList.contains('closed');

        const absolutePos = this.getAbsolutePosition(this.fieldElement as HTMLElement);

        if (isOpen) {
            setTimeout(() => {
                calendarContainer.classList.add('closed');
                calendarContainer.classList.remove('open');
            }, 200);
        } else {
            calendarContainer.classList.remove('closed');
            calendarContainer.classList.add('open');
            calendarContainer.style.width = '100%';
            calendarContainer.style.top = absolutePos.top;
            calendarContainer.style.left = absolutePos.left;
        }
    }

    onDocumentClick(event: MouseEvent) {
        if (!this.dateRangePicker) return;

        // Necessary so that the default behavior is suppressed when clicking on the label
        if ((event.target instanceof HTMLLabelElement) && (event.target.closest('tk-form-timepicker'))) {
            event.preventDefault();
        }

        // return if target is form element
        if ((event.target as HTMLElement).closest('tk-form-timepicker')
            || (event.target as HTMLElement).closest('.flatpickr-calendar')) return;

        const { calendarContainer } = this.dateRangePicker;

        const missingClassClosed = !calendarContainer.classList.contains('open')
            && !calendarContainer.classList.contains('closed');

        if (missingClassClosed) {
            calendarContainer.classList.add('closed');
        }

        if (!calendarContainer.classList.contains('open')) return;
        if (event && event.target && (event.target as HTMLElement).closest('tk-form-timepicker')) return;

        setTimeout(() => {
            calendarContainer.classList.add('closed');
            calendarContainer.classList.remove('open');
        }, 200);
    }

    getAbsolutePosition(element: HTMLElement) {
        // get elements relatove position to viewport
        const rect = element.getBoundingClientRect();

        // get documents scroll position
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        const inputHeight = (this.fieldElement as HTMLElement).offsetHeight;

        const gapDistance = 2;

        // calculate absolute position
        const absoluteLeft = `${rect.left + scrollLeft}px`;
        const absoluteTop = `${rect.top + scrollTop + inputHeight + gapDistance}px`;

        return { left: absoluteLeft, top: absoluteTop };
    }
}
